import { useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useScene } from '../scene-context';

export interface LightDiyaProps {
  children: ReactNode;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
}

export function LightDiya({
  children,
  primaryColor,
  secondaryColor,
  backgroundColor,
  textColor,
}: LightDiyaProps) {
  let colors = {
    primary: primaryColor || '#BE123C',
    secondary: secondaryColor || '#D4AF37',
    background: backgroundColor || '#1A202C',
    text: textColor || '#F7FAFC',
  };

  try {
    const scene = useScene();
    if (scene?.pack?.colors) {
      colors = {
        primary: primaryColor || scene.pack.colors.primary,
        secondary: secondaryColor || scene.pack.colors.secondary,
        background: backgroundColor || scene.pack.colors.background,
        text: textColor || scene.pack.colors.text,
      };
    }
  } catch {
    // Standalone fallback when rendered outside SceneProvider
  }

  const reduceMotion = useReducedMotion();
  const [lit, setLit] = useState(Boolean(reduceMotion));

  return (
    <div className="relative z-10 flex w-full max-w-3xl flex-col items-center px-6 text-center mx-auto">
      <button
        type="button"
        onClick={() => setLit(true)}
        className="relative mb-8 flex h-24 w-24 items-end justify-center rounded-full border cursor-pointer"
        style={{ borderColor: `${colors.primary}55`, background: `${colors.background}88` }}
        aria-label="Light the diya"
      >
        <motion.span
          className="absolute bottom-11 h-12 w-8 rounded-full"
          style={{
            background: lit
              ? `radial-gradient(circle, #fff7c0 0%, ${colors.primary} 38%, transparent 72%)`
              : 'transparent',
            boxShadow: lit ? `0 0 42px ${colors.primary}` : 'none',
          }}
          animate={lit && !reduceMotion ? { scale: [0.9, 1.18, 0.94], opacity: [0.82, 1, 0.86] } : {}}
          transition={{ repeat: Infinity, duration: 1.3 }}
        />
        <span
          className="mb-5 h-7 w-14 rounded-b-full rounded-t-sm"
          style={{ background: `linear-gradient(90deg, ${colors.secondary}, ${colors.primary})` }}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ opacity: lit ? 1 : 0.95 }}
        className="w-full"
      >
        {children}
      </motion.div>
      {!lit && (
        <p className="mt-5 text-xs font-semibold uppercase tracking-wider" style={{ color: colors.primary }}>
          Tap diya to light the sacred flame
        </p>
      )}
    </div>
  );
}
